/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { Suspense, useState, useEffect } from 'react';
import { useAiContext } from '../providers/ai-provider';
import { AiSignal, SmartMoneyTrader } from '../types/ai';
import { 
    ArrowLeft, Brain, Activity, Zap, TrendingUp, Power, HelpCircle, X,
    Shield, Bot, BarChart, Settings, ExternalLink, Eye, User, Sparkles, ArrowRight, RefreshCw, Pause
} from 'lucide-react';
import { formatCurrency, formatCompact } from '../lib/utils/format';
import { AiTradingAgentSettingsModal } from '../components/AiTradingAgentSettingsModal';
import { AiEngine, SwarmStatus } from '../lib/ai/engine';
import { AiSignalsFeed } from '../components/AiSignalsFeed';

// Lazy load heavy interaction components
const TradeModal = React.lazy(() => import('../components/TradeModal').then(m => ({ default: m.TradeModal })));
const AnalysisPopupCard = React.lazy(() => import('../components/analysis-popup-card').then(m => ({ default: m.AnalysisPopupCard })));

interface AiDeskProps {
    onBack: () => void;
    onLaunchAssistant: () => void;
}

const HowItWorksModal = ({ onClose }: { onClose: () => void }) => (
    <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
        <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl p-6 border border-stone-200" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-serif font-bold text-stone-900 flex items-center gap-2">
                    <Brain size={24} className="text-nobel-gold" /> Swarm Engine Workflow
                </h3>
                <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-full text-stone-400 hover:text-stone-900" title="Close">
                    <X size={20} />
                </button>
            </div>
            
            <div className="space-y-6 relative">
                <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-stone-200"></div>
                
                <div className="relative pl-12">
                    <div className="absolute left-0 top-0 w-8 h-8 bg-stone-900 text-white rounded-full flex items-center justify-center font-bold text-sm shadow-md z-10">1</div>
                    <h4 className="font-bold text-stone-900 mb-1">Deep Scan</h4>
                    <p className="text-sm text-stone-600 leading-relaxed">
                        The engine continuously monitors mempool and block data on Solana and Sui, looking for volume spikes and smart money wallet movements.
                    </p>
                </div>

                <div className="relative pl-12">
                    <div className="absolute left-0 top-0 w-8 h-8 bg-white border-2 border-stone-900 text-stone-900 rounded-full flex items-center justify-center font-bold text-sm shadow-md z-10">2</div>
                    <h4 className="font-bold text-stone-900 mb-1">Swarm Consensus</h4>
                    <p className="text-sm text-stone-600 leading-relaxed">
                        Hundreds of virtual nodes analyze price action independently. A signal is only generated when <strong>Consensus Rate &gt; 80%</strong>.
                    </p>
                </div>

                <div className="relative pl-12">
                    <div className="absolute left-0 top-0 w-8 h-8 bg-white border-2 border-stone-300 text-stone-500 rounded-full flex items-center justify-center font-bold text-sm shadow-sm z-10">3</div>
                    <h4 className="font-bold text-stone-900 mb-1">Risk Filter</h4>
                    <p className="text-sm text-stone-600 leading-relaxed">
                        Signals pass through a safety layer that checks for Honey Pots, Mint Authority, and Liquidity Locks before being displayed.
                    </p>
                </div>

                <div className="relative pl-12">
                    <div className="absolute left-0 top-0 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-sm shadow-md z-10">4</div>
                    <h4 className="font-bold text-stone-900 mb-1">Execution</h4>
                    <p className="text-sm text-stone-600 leading-relaxed">
                        Manually trade with one click, or enable <strong>Auto-Trader</strong> to let the bot execute entries and exits based on your risk settings.
                    </p>
                </div>
            </div>

            <div className="mt-8 pt-4 border-t border-stone-100 flex flex-end">
                <button onClick={onClose} className="ml-auto px-6 py-2.5 bg-stone-900 text-white rounded-lg font-bold text-sm hover:bg-stone-800">
                    Got it
                </button>
            </div>
        </div>
    </div>
);

export const AiDesk: React.FC<AiDeskProps> = ({ onBack, onLaunchAssistant }) => {
    const { signals, performance, isLoading, isBotActive, toggleBot, agentSettings, updateAgentSettings, smartMoney, refreshData } = useAiContext();
    const [selectedSignal, setSelectedSignal] = useState<AiSignal | null>(null);
    const [tradeModalFromSignal, setTradeModalFromSignal] = useState<AiSignal | null>(null);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [chainFilter, setChainFilter] = useState<'ALL' | 'SOL' | 'SUI'>('ALL');
    const [showHowItWorks, setShowHowItWorks] = useState(false);
    const [swarmStatus, setSwarmStatus] = useState<SwarmStatus | null>(null);

    useEffect(() => {
        const fetchStatus = async () => {
            const status = await AiEngine.getSwarmStatus();
            setSwarmStatus(status);
        };
        fetchStatus();
        const interval = setInterval(fetchStatus, 5000);
        return () => clearInterval(interval);
    }, []);

    const filteredSignals = signals.filter(s => {
        if (chainFilter === 'ALL') return true;
        return s.token.properties?.chain === chainFilter;
    });

    return (
        <div className="min-h-screen bg-[#F9F8F4] flex flex-col relative overflow-hidden">
            <Suspense fallback={null}>
                {selectedSignal && (
                    <AnalysisPopupCard 
                        signal={selectedSignal} 
                        onClose={() => setSelectedSignal(null)}
                        onExecute={() => { setTradeModalFromSignal(selectedSignal); setSelectedSignal(null); }}
                    />
                )}
                {tradeModalFromSignal && (
                     <TradeModal 
                        token={tradeModalFromSignal.token}
                        currentPrice={tradeModalFromSignal.priceAtSignal}
                        isOpen={!!tradeModalFromSignal}
                        onClose={() => setTradeModalFromSignal(null)}
                        initialSide={tradeModalFromSignal.type}
                     />
                )}
            </Suspense>

             <AiTradingAgentSettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} currentSettings={agentSettings} onSave={updateAgentSettings} />
             {showHowItWorks && <HowItWorksModal onClose={() => setShowHowItWorks(false)} />}

            <main className="container mx-auto px-6 py-8 relative z-10 flex-1">
                {/* Header */}
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-12 gap-8 relative">
                    <button onClick={() => setShowHowItWorks(true)} className="absolute top-0 right-0 lg:top-[-20px] lg:right-0 text-xs font-bold text-stone-400 hover:text-stone-900 flex items-center gap-1.5 uppercase tracking-wide transition-colors">
                        <HelpCircle size={14} /> Guide
                    </button>

                    <div className="max-w-2xl pt-6 lg:pt-0">
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-stone-900 text-nobel-gold text-[10px] font-bold tracking-widest uppercase rounded-full mb-4 border border-stone-700 shadow-lg">
                            <Brain size={12} /> Neural Engine v2.2
                        </div>
                        <h1 className="font-serif text-4xl md:text-5xl text-stone-900 mb-3 leading-tight">AI Command Center</h1>
                        <p className="text-stone-500 text-base md:text-lg leading-relaxed">
                            Monitor swarm intelligence signals and autonomous agent performance.
                        </p>
                    </div>
                    
                    {/* Bot Toggle */}
                    <div className="flex-shrink-0 w-full lg:w-auto">
                        <div className="bg-white p-2 rounded-2xl border border-stone-200 shadow-xl flex items-center gap-4 pr-6 relative overflow-hidden">
                            {isBotActive && <div className="absolute inset-0 bg-green-50/50 animate-pulse pointer-events-none"></div>}
                            <button 
                                onClick={toggleBot}
                                className={`relative w-16 h-16 rounded-xl flex items-center justify-center transition-all duration-300 group z-10 ${isBotActive ? 'bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg text-white' : 'bg-stone-100 text-stone-400 hover:bg-stone-200'}`}
                            >
                                {isBotActive ? <Pause size={28} className="fill-current" /> : <Power size={28} />}
                            </button>
                            <div className="flex flex-col z-10">
                                <div className="text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-0.5">Agent Status</div>
                                <div className={`font-mono font-bold text-lg flex items-center gap-2 ${isBotActive ? 'text-green-600' : 'text-stone-500'}`}>
                                    {isBotActive ? 'ONLINE' : 'STANDBY'}
                                </div>
                                {swarmStatus && <div className="text-[9px] font-mono text-stone-400 mt-1">Nodes: {swarmStatus.activeNodes}</div>}
                            </div>
                        </div>
                    </div>
                </div>

                {/* AI Assistant Banner (New Product) */}
                <div className="mb-12 relative group cursor-pointer" onClick={onLaunchAssistant}>
                    <div className="absolute inset-0 bg-gradient-to-r from-stone-900 via-stone-800 to-stone-900 rounded-3xl shadow-2xl transform transition-transform group-hover:scale-[1.01]"></div>
                    <div className="relative p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-8">
                        <div className="flex items-start gap-6">
                            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-white/20 text-nobel-gold shadow-inner shrink-0">
                                <Sparkles size={32} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-serif font-bold text-white mb-2">Launch Neural Assistant</h2>
                                <p className="text-stone-400 text-sm max-w-lg leading-relaxed">
                                    Access the new conversational interface. Ask complex strategy questions, analyze token contracts deep-dive, or simulate risk scenarios with Gemini 3.0 Pro.
                                </p>
                            </div>
                        </div>
                        <button className="px-8 py-4 bg-nobel-gold text-stone-900 rounded-xl font-bold text-sm hover:bg-white transition-colors shadow-lg flex items-center gap-2 whitespace-nowrap shrink-0">
                            Open Chat Interface <ArrowRight size={16} />
                        </button>
                    </div>
                </div>

                {/* Performance Stats */}
                {performance && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12">
                        <div className="bg-white p-5 rounded-2xl border border-stone-200 shadow-sm">
                            <div className="text-[10px] font-bold text-stone-400 uppercase mb-2 flex items-center gap-2"><Activity size={12}/> Total Profit</div>
                            <div className="font-serif text-2xl text-stone-900">{formatCurrency(performance.totalProfit)}</div>
                        </div>
                        <div className="bg-white p-5 rounded-2xl border border-stone-200 shadow-sm">
                            <div className="text-[10px] font-bold text-stone-400 uppercase mb-2 flex items-center gap-2"><Zap size={12}/> Win Rate</div>
                            <div className="font-serif text-2xl text-stone-900">{performance.winRate}%</div>
                        </div>
                        <div className="bg-white p-5 rounded-2xl border border-stone-200 shadow-sm">
                            <div className="text-[10px] font-bold text-stone-400 uppercase mb-2 flex items-center gap-2"><TrendingUp size={12}/> 24h PnL</div>
                            <div className="font-serif text-2xl text-stone-900">{formatCurrency(performance.dailyPnl)}</div>
                        </div>
                        <button onClick={() => setIsSettingsOpen(true)} className="bg-stone-900 p-5 rounded-2xl border border-stone-800 text-stone-200 relative overflow-hidden group text-left">
                            <div className="absolute -right-4 -top-4 w-20 h-20 bg-nobel-gold/10 rounded-full blur-xl group-hover:bg-nobel-gold/20 transition-all"></div>
                            <div className="text-[10px] font-bold text-stone-500 uppercase mb-2 flex items-center gap-2 relative z-10"><Settings size={12}/> Strategy</div>
                            <div className="font-serif text-xl text-white mb-1 relative z-10">{agentSettings.riskLevel}</div>
                            <div className="text-[10px] text-stone-500 font-bold relative z-10">Size: {agentSettings.tradeSize} SOL</div>
                        </button>
                    </div>
                )}

                <div className="w-full">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="font-serif text-2xl text-stone-900">Latest Signals</h3>
                        <div className="flex gap-2">
                            <button onClick={() => setChainFilter('ALL')} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${chainFilter === 'ALL' ? 'bg-stone-900 text-white' : 'bg-white border border-stone-200 text-stone-500'}`}>All</button>
                            <button onClick={() => setChainFilter('SOL')} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${chainFilter === 'SOL' ? 'bg-stone-900 text-white' : 'bg-white border border-stone-200 text-stone-500'}`}>SOL</button>
                            <button onClick={() => setChainFilter('SUI')} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${chainFilter === 'SUI' ? 'bg-stone-900 text-white' : 'bg-white border border-stone-200 text-stone-500'}`}>SUI</button>
                            <button onClick={refreshData} className="p-1.5 bg-white border border-stone-200 rounded-lg text-stone-400 hover:text-stone-900"><RefreshCw size={14} className={isLoading ? 'animate-spin' : ''}/></button>
                        </div>
                    </div>
                    <AiSignalsFeed chainFilter={chainFilter} />
                </div>
            </main>
        </div>
    );
};